// NavigationButton.tsx
import React from 'react';
import { SvgIconComponent } from '@mui/icons-material';
import { Box, useTheme, Tooltip } from '@mui/material';

interface NavigationButtonProps {
  icon: SvgIconComponent;
  text: string;
  isSelected: boolean;
  disabled?: boolean;
  onClick: () => void;
  /** Show a subtle running indicator (e.g. pulsing dot) */
  showRunningIndicator?: boolean;
}

const WebNavButton: React.FC<NavigationButtonProps> = ({
  icon: Icon,
  text,
  isSelected,
  disabled,
  onClick,
  showRunningIndicator = false
}) => {
  const theme = useTheme();
  const transition = '0.3s cubic-bezier(0.4, 0, 0.2, 1)';

  return (
    <Tooltip title={text} placement="right" arrow>
      <Box
        onClick={disabled ? undefined : onClick}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: isSelected
            ? theme.customColors.themePrimary
            : theme.customColors.themeNeutralPrimaryColor,
          marginBottom: '2px',
          borderRadius: '8px',
          backgroundColor: isSelected
            ? theme.palette.action.selected
            : 'transparent',
          transition: `all ${transition}`,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          ...(!disabled && {
            '&:hover': {
              color: theme.customColors.themePrimary,
              backgroundColor: theme.palette.action.hover
            }
          })
        }}
      >
        <Box
          sx={{
            width: '24px',
            height: '24px',
            minWidth: '24px',
            flexShrink: 0,
            padding: '4px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            transition: `margin-right ${transition}`
          }}
        >
          <Icon sx={{ fontSize: '24px' }} />
          {showRunningIndicator && (
            <Box
              sx={{
                position: 'absolute',
                top: 2,
                right: 2,
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor:
                  theme.customColors.themePrimary || 'primary.main',
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.4 }
                }
              }}
            />
          )}
        </Box>
      </Box>
    </Tooltip>
  );
};

export default WebNavButton;
